import { useCallback, useMemo, useState } from 'react';
import { Box, Button, DialogActions, Typography } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import type { CellValueChangedEvent } from 'ag-grid-community/dist/types/core/events';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS, extrapolateMonthlyMap, horizonTargetMonths } from '@callmart/types';
import ResponsiveDialog from '../UI/Dialogs/ResponsiveDialog';
import TableGridItem from '../UI/TableGridItem';
import { BillingConfigWithRelations } from '../../services/budgetBillingConfig';
import {
    useBudgetSummary,
    useDeleteWarningAck,
    useSaveWarningAck,
    useUpdateBillingConfigsBulk,
    useUpdateForecastVolumesBulk,
} from '../../hooks/api/budgetBillingConfig';
import { useDispatchErrorNotification } from '../../hooks/useDispatchErrorNotification';
import { usePendingCellEdits } from '../../hooks/usePendingCellEdits';
import { billingConfigPayload } from '../../hooks/usePendingConfigEdits';
import { useBudgetContext } from '../../store/Budget/BudgetContext';
import { queryClient } from '../../services/queryClient';
import { QueryKey } from '../../enums/QueryKey';
import { useBudgetPeriodMonths } from './useBudgetPeriodMonths';
import { useRowStatus } from './useRowStatus';
import { forecastPayload } from './overviewEditPayloads';
import { calculateAllCosts } from './budgetCalculation';
import {
    currencyFormatter,
    formatWholeEuro,
    generateMonthColumns,
    getContiguousMonths,
    integerFormatter,
    parseBudgetCell,
    priceFormatter,
    MONTH_KEY,
} from './helpers';
import CellLegend from './CellLegend';
import {
    ABT_OVERRIDE_CELL_BG,
    DEFAULT_VALUE_TEXT,
    EDITABLE_CELL_BG,
    MANUAL_ACCENT_SHADOW,
    MISSING_CELL_BG,
    pendingBorder,
} from './pendingCellStyle';
import dayjs from '@utils/dayjs';

/** = FORECAST_EDIT_KIND in ForecastVolumeGrid — a literal here because importing it from the grid
 *  would make the two modules circular (the grid renders this dialog). */
const VOLUME_KIND = 'forecastVolumeEdits';

export interface ConfigDetailDialogData {
    config: BillingConfigWithRelations;
    /** The Overview's unsaved volume edits for this config. They are the dialog's DISPLAY BASE and
     *  the pristine values its own edits are measured against — so the dialog shows what the page
     *  shows, and retyping the served value here counts as a change that, saved, reverts the page's
     *  edit (via onSaved dropping exactly the saved months from the page store). They are NOT
     *  claimed as dialog edits: an untouched seeded month stays the page's to save or discard. */
    pendingVolumes?: Map<string, number | null>;
}

interface ConfigDetailDialogProps {
    /** The config whose months are being edited; null closes the dialog. */
    data: ConfigDetailDialogData | null;
    versionId: number | undefined;
    onClose: () => void;
    /** Fired after a successful save, before closing — the page drops its own pending volume edits
     *  for exactly the months this save wrote, which now supersede them. */
    onSaved?: (configId: number, savedVolumeMonths: string[]) => void;
}

// CALL-818: one working unit location's months on one surface, behind the row's status icon. The
// Overview edits volumes and the Prices & ABT page edits the three rate series, so completing a
// single partner's row means hopping pages; here the row is transposed instead — data series as
// rows, the funded budget period as columns — with a computed cost row so the effect of an entry is
// visible before it is saved. The CALL-816 acknowledgement lives HERE too (this dialog replaced the
// separate confirmation dialog): the grid shows exactly which months are incomplete, and the user
// either fills them in and saves, or accepts them and acknowledges — the two answers to one warning.
interface DetailRowDef {
    /** Doubles as the pending-edit kind — exactly the payload field the batch requests carry. */
    rowKey: string;
    label: string;
}

const COST_ROW = 'cost';

const formatMonths = (months: string[]): string =>
    months.map((month) => dayjs(month, DATE_FORMATS.ISO_DATE).format('MMM YY')).join(', ');

const ConfigDetailDialogInner = ({
    data,
    versionId,
    onClose,
    onSaved,
}: ConfigDetailDialogProps & { data: ConfigDetailDialogData }) => {
    const { t } = useTranslation('budget');
    const { isReadOnly, showExtrapolation } = useBudgetContext();
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { config, pendingVolumes } = data;

    // The dialog's own pending set. The component remounts per open (keyed by version + config
    // below), so the store starts empty and closing the dialog is the discard.
    const pending = usePendingCellEdits(config.id);

    const { mutateAsync: saveConfigs } = useUpdateBillingConfigsBulk();
    const { mutateAsync: saveVolumes } = useUpdateForecastVolumesBulk(versionId);
    const [isSaving, setIsSaving] = useState(false);

    // CALL-816: the row's live verdict and acknowledgement — the SAME computation the status icon
    // reads, so this dialog can never disagree with the triangle behind it. Live, not captured at
    // open: a save from here refetches the configs, and the missing list below updates with them.
    const rowStatus = useRowStatus(versionId);
    const ackInfo = rowStatus.get(config.id)?.ackInfo;
    const ack = ackInfo?.ack;
    const isAcked = Boolean(ack && ackInfo?.covered);
    const { mutateAsync: saveAck, isLoading: isAcking } = useSaveWarningAck(versionId);
    const { mutateAsync: deleteAck, isLoading: isRemovingAck } = useDeleteWarningAck(versionId);

    const isBusy = isSaving || isAcking || isRemovingAck;

    // CALL-816's axis: the funded budget period, so completing the dialog is exactly what turns the
    // row's warning green. Falls back to the config's own data span (or the current year) while no
    // budget is entered yet, so a fresh draft still gets columns. While the summary query is still
    // answering (or failed), the axis on screen is a guess — editing waits (see canEdit), or a cell
    // typed into the guessed axis would silently survive under the real columns and still be saved.
    const periodMonths = useBudgetPeriodMonths(versionId);
    const { isLoading: isPeriodLoading, isError: isPeriodError } = useBudgetSummary(versionId);

    // Edits are frozen while any request is out: a cell changed after pending.submit would not be in
    // the submitted payload, and the close-on-success would silently discard it. The versionId check
    // is belt-and-braces — without a selected version the Overview has no rows to open this from,
    // and the disabled summary query keeps isPeriodLoading true — but the save/ack mutations assert
    // versionId!, so the gate should not depend on a disabled-query quirk to hold.
    const canEdit = versionId != null && !isReadOnly && !isBusy && !isPeriodLoading && !isPeriodError;

    const served = useMemo(
        () => ({
            [VOLUME_KIND]: new Map((config.forecastVolumes || []).map((fv) => [String(fv.month), Number(fv.volume)])),
            minutePriceEdits: new Map((config.minutePrices || []).map((mp) => [String(mp.month), Number(mp.price)])),
            contractABTEdits: new Map(
                (config.contractABTs || []).map((abt) => [String(abt.month), Number(abt.abtSeconds)])
            ),
            bonusEdits: new Map((config.bonuses || []).map((bonus) => [String(bonus.month), Number(bonus.amount)])),
        }),
        [config]
    );

    // The volume row's base: served values with the page's unsaved edits laid over them (see the
    // pendingVolumes doc above). The other series have no page-side edits — served IS their base.
    const volumeBase = useMemo(() => {
        const base = new Map(served[VOLUME_KIND]);
        for (const [month, value] of pendingVolumes ?? []) {
            if (value === null) base.delete(month);
            else base.set(month, Number(value));
        }
        return base;
    }, [served, pendingVolumes]);
    const baseFor = useCallback(
        (kind: string) => (kind === VOLUME_KIND ? volumeBase : served[kind as keyof typeof served]),
        [volumeBase, served]
    );

    const months = useMemo(() => {
        if (periodMonths.length) return periodMonths;
        return getContiguousMonths([...Object.values(served).flatMap((byMonth) => [...byMonth.keys()])]);
    }, [periodMonths, served]);

    // The STORED source per volume month — only a stored manual month is the user's to delete
    // (same rule as the Handlingdaten grid; an auto month's clear restores the stored value).
    const servedVolumeSources = useMemo(
        () => new Map((config.forecastVolumes || []).map((fv) => [String(fv.month), fv.source ?? 'auto'])),
        [config]
    );

    const rowDefs = useMemo<DetailRowDef[]>(
        () => [
            { rowKey: VOLUME_KIND, label: t('field.handlingData') },
            // A flat-fee row's cost IS its monthly amount — prices and ABT play no part, so their rows
            // would only invite entries the calculation ignores.
            ...(config.flatFee
                ? []
                : [
                      { rowKey: 'minutePriceEdits', label: t('prices.minutePrices') },
                      { rowKey: 'contractABTEdits', label: t('prices.contractAbt') },
                  ]),
            { rowKey: 'bonusEdits', label: t('prices.bonus') },
            { rowKey: COST_ROW, label: t('summary.handlingCost') },
        ],
        [t, config.flatFee]
    );

    // Base + the dialog's pending edits, per series — what the rows show and what the cost reads.
    const effective = useMemo(() => {
        const bySeries = new Map<string, Map<string, number>>();
        for (const kind of Object.keys(served)) {
            const merged = new Map(baseFor(kind));
            for (const edit of pending.edits) {
                if (edit.kind !== kind) continue;
                if (edit.value === null) merged.delete(edit.key);
                else merged.set(edit.key, Number(edit.value));
            }
            bySeries.set(kind, merged);
        }
        return bySeries;
    }, [served, baseFor, pending.edits]);

    // CALL-818: mark the exact cells the warning is about. Column membership follows the SAVED
    // verdict (the same canonical missing set the tooltip and the list below name); within such a
    // month, a cell is a gap by getRowCompleteness's own rules — volume/price ≤ 0, ABT ≤ 0 unless
    // defaultAbt covers it, bonus only on flat-fee rows. The value side reads the EFFECTIVE (edited)
    // series, so a gap stops being tinted the moment the user types into it.
    const missingMonthSet = useMemo(() => new Set(ackInfo?.canonicalMissing ?? []), [ackInfo]);
    const cellNeedsFill = useCallback(
        (rowKey: string, month: string): boolean => {
            if (!missingMonthSet.has(month)) return false;
            const raw = effective.get(rowKey)?.get(month);
            const value = raw ?? 0;
            switch (rowKey) {
                case 'bonusEdits':
                    return Boolean(config.flatFee) && value <= 0;
                // getRowCompleteness's rules: ABT needs a positive value, entered or Standard-ABT (an
                // entered 0 is a gap even with a default); a price only needs to be PRESENT — a typed 0,
                // monthly or default, is a real price (CALL-824).
                case 'contractABTEdits':
                    return raw != null ? value <= 0 : Number(config.defaultAbt ?? 0) <= 0;
                case 'minutePriceEdits':
                    return raw == null && config.defaultMinutePrice == null;
                case VOLUME_KIND:
                    return value <= 0;
                default:
                    return false;
            }
        },
        [missingMonthSet, effective, config.flatFee, config.defaultAbt, config.defaultMinutePrice]
    );

    // CALL-822/824: an entered Vertrags-ABT or Minutenpreis that differs from the row's default wins in the
    // cost calculation — the same cue as the Preise & ABT grid. Reads the EFFECTIVE series, so typing the
    // value back to the default clears it on the keystroke. Returns the tooltip naming the overridden
    // value, or null when nothing diverges (no default, or default ≤ 0, means nothing to diverge from).
    const overrideTooltip = useCallback(
        (rowKey: string, month: string): string | null => {
            let reference: number | null = null;
            let tooltip: ((value: string | number) => string) | null = null;
            if (rowKey === 'contractABTEdits') {
                // A Standard-ABT of 0 is "no reference" (only a positive one is valid), see ContractABTGrid.
                reference =
                    config.defaultAbt == null || Number(config.defaultAbt) <= 0 ? null : Number(config.defaultAbt);
                tooltip = (value) => t('prices.overridesDefaultAbt', { value });
            } else if (rowKey === 'minutePriceEdits') {
                // A typed 0 is a real default (CALL-824), so the gate is "has a default", not "> 0".
                reference = config.defaultMinutePrice == null ? null : Number(config.defaultMinutePrice);
                tooltip = (value) =>
                    t('prices.overridesDefaultMinutePrice', { value: priceFormatter.format(Number(value)) });
            }
            if (!tooltip || reference == null) return null;
            const value = effective.get(rowKey)?.get(month);
            return value != null && value !== reference ? tooltip(reference) : null;
        },
        [config.defaultAbt, config.defaultMinutePrice, effective, t]
    );

    // CALL-824: the default a month WITHOUT an entered value is charged at — shown greyed in the empty
    // cell so the row does not read as missing data. Only for real month columns (never the Total),
    // only when nothing is entered, and only when the default counts (any price incl. 0; ABT > 0).
    // The months the projection reaches — the SAME rule as the cost row (calculateAllCosts): targets are
    // this config's forecast-volume months within the 6-month horizon past the last entered price, ABT
    // over the same targets. There the Kosten row is charged the projected rate, not the default, so the
    // greyed default must not be shown for them, or it names a number the cost did not use.
    const projectedMonths = useMemo(() => {
        if (!showExtrapolation) return { minutePriceEdits: new Set<string>(), contractABTEdits: new Set<string>() };
        const priceMap = effective.get('minutePriceEdits') ?? new Map<string, number>();
        const abtMap = effective.get('contractABTEdits') ?? new Map<string, number>();
        const volumeMonths = [...(effective.get(VOLUME_KIND) ?? new Map<string, number>()).keys()];
        const targetMonths = horizonTargetMonths(priceMap, volumeMonths);
        return {
            minutePriceEdits: new Set(extrapolateMonthlyMap(priceMap, targetMonths).extrapolatedMonths),
            contractABTEdits: new Set(extrapolateMonthlyMap(abtMap, targetMonths).extrapolatedMonths),
        };
    }, [showExtrapolation, effective]);

    const fallbackFor = useCallback(
        (rowKey: string, month: string): number | null => {
            if (!months.includes(month) || effective.get(rowKey)?.get(month) != null) return null;
            if (rowKey === 'minutePriceEdits' || rowKey === 'contractABTEdits') {
                if (projectedMonths[rowKey].has(month)) return null;
            }
            if (rowKey === 'minutePriceEdits') {
                return config.defaultMinutePrice == null ? null : Number(config.defaultMinutePrice);
            }
            if (rowKey === 'contractABTEdits') {
                return config.defaultAbt == null || Number(config.defaultAbt) <= 0 ? null : Number(config.defaultAbt);
            }
            return null;
        },
        [months, effective, projectedMonths, config.defaultAbt, config.defaultMinutePrice]
    );

    // The cost row comes from the SAME calculation as the page's Kosten view and the summary,
    // extrapolation toggle included, fed a clone of the config carrying the dialog's unsaved months —
    // so an entry moves the cost before it is saved. Only the month/value fields matter to the
    // calculation, hence the cast.
    const costByMonth = useMemo(() => {
        const merged = {
            ...config,
            forecastVolumes: [...effective.get(VOLUME_KIND)!].map(([month, volume]) => ({ month, volume })),
            minutePrices: [...effective.get('minutePriceEdits')!].map(([month, price]) => ({ month, price })),
            contractABTs: [...effective.get('contractABTEdits')!].map(([month, abtSeconds]) => ({
                month,
                abtSeconds,
            })),
            bonuses: [...effective.get('bonusEdits')!].map(([month, amount]) => ({ month, amount })),
        } as unknown as BillingConfigWithRelations;
        return calculateAllCosts([merged], { extrapolate: showExtrapolation })[0].monthlyResults;
    }, [config, effective, showExtrapolation]);

    const rowData = useMemo(
        () =>
            rowDefs.map(({ rowKey, label }) => {
                const row: Record<string, unknown> = { rowKey, label };
                let sum = 0;
                for (const month of months) {
                    const value =
                        rowKey === COST_ROW
                            ? costByMonth.get(month)?.calculatedCost
                            : effective.get(rowKey)?.get(month);
                    row[month] = value;
                    sum += Number(value ?? 0);
                }
                // A summed price or ABT is not a figure anyone uses — leave their total blank.
                row.__sum = rowKey === 'minutePriceEdits' || rowKey === 'contractABTEdits' ? null : sum;
                return row;
            }),
        [rowDefs, months, effective, costByMonth]
    );

    const formatByRow = useCallback(
        (params: { data?: { rowKey?: string }; value: unknown; colDef?: { field?: string } }): string => {
            const rowKey = params.data?.rowKey;
            const field = params.colDef?.field;
            let { value } = params;
            if ((value == null || value === '') && rowKey && field) value = fallbackFor(rowKey, field);
            switch (rowKey) {
                case COST_ROW:
                    return formatWholeEuro(value);
                case 'minutePriceEdits':
                    return value == null || value === '' ? '' : priceFormatter.format(Number(value));
                case 'bonusEdits':
                    return value == null || value === '' ? '' : currencyFormatter.format(Number(value));
                default:
                    return value == null || value === '' ? '' : integerFormatter.format(Number(value));
            }
        },
        [fallbackFor]
    );

    const monthColumns = useMemo(
        () =>
            generateMonthColumns(months, {
                minWidth: 100,
                editable: (params) => canEdit && (params.data as DetailRowDef)?.rowKey !== COST_ROW,
                useValueFormatterForExport: false,
                valueFormatter: formatByRow,
                valueParser: (params) => {
                    const parsed = parseBudgetCell(params.newValue);
                    const rowKey = (params.data as DetailRowDef)?.rowKey;
                    if (parsed !== null) {
                        // Volumes are whole calls, ABTs whole seconds — same rounding as their grids.
                        return rowKey === VOLUME_KIND || rowKey === 'contractABTEdits' ? Math.round(parsed) : parsed;
                    }
                    // Clearing a volume follows the Handlingdaten rule: a stored manual month is deleted,
                    // a stored auto month gets its stored value back (recorded against the seeded base,
                    // so a page edit reverts on save). There are no projections here, so nothing-stored
                    // simply stays empty.
                    if (rowKey === VOLUME_KIND) {
                        const field = params.colDef.field as string;
                        const stored = served[VOLUME_KIND].get(field);
                        if (stored !== undefined && servedVolumeSources.get(field) !== 'manual') return stored;
                    }
                    return null;
                },
                cellStyle: (params: any): Record<string, string> => {
                    const style: Record<string, string> = { textAlign: 'right' };
                    const rowKey = params.data?.rowKey as string | undefined;
                    const field = params.colDef?.field as string | undefined;
                    // CALL-824: a greyed default in an empty cell, whether or not the dialog is editable.
                    if (rowKey && field && params.value == null && fallbackFor(rowKey, field) != null) {
                        style.color = DEFAULT_VALUE_TEXT;
                        style.fontStyle = 'italic';
                    }
                    if (!rowKey || rowKey === COST_ROW || isReadOnly) return style;

                    // A gap the warning names is tinted; every other editable cell stays yellow.
                    const isGap = Boolean(field && cellNeedsFill(rowKey, field));
                    style.backgroundColor = isGap ? MISSING_CELL_BG : EDITABLE_CELL_BG;
                    // CALL-822/824: an entered value overriding the row's default (see overrideTooltip).
                    // A gap the warning names keeps its tint — an entered 0 over a default is both, and
                    // the gap is what the user has to act on.
                    if (field && !isGap && overrideTooltip(rowKey, field) !== null) {
                        style.backgroundColor = ABT_OVERRIDE_CELL_BG;
                    }
                    const pendingCell = field ? pending.get(config.id, rowKey, field) : undefined;
                    Object.assign(style, pendingBorder(pendingCell !== undefined));
                    // The manual-override accent, same cue as the Handlingdaten grid: stored manual
                    // months carry it, so does every pending edit (that is what the save stores), and
                    // so does a month seeded from the page's unsaved edits (the page shows it manual).
                    if (
                        rowKey === VOLUME_KIND &&
                        field &&
                        (pendingCell !== undefined ||
                            servedVolumeSources.get(field) === 'manual' ||
                            pendingVolumes?.get(field) != null)
                    ) {
                        style.boxShadow = MANUAL_ACCENT_SHADOW;
                    }
                    return style;
                },
                // CALL-822/824: the tinted override cell names the value it overrides, like the grids.
                tooltipValueGetter: (params) => {
                    const rowKey = (params.data as { rowKey?: string })?.rowKey;
                    const field = params.colDef && 'field' in params.colDef ? params.colDef.field : undefined;
                    if (!rowKey || !field) return null;
                    if (fallbackFor(rowKey, field) != null) return t('prices.fallbackFromDefault');
                    return overrideTooltip(rowKey, field);
                },
            }),
        [
            months,
            canEdit,
            isReadOnly,
            formatByRow,
            served,
            servedVolumeSources,
            pendingVolumes,
            pending.get,
            config.id,
            overrideTooltip,
            fallbackFor,
            cellNeedsFill,
            effective,
            t,
        ]
    );

    const columnDefs = useMemo<ColDef[]>(
        () => [
            {
                headerName: '',
                field: 'label',
                minWidth: 160,
                pinned: 'left',
                editable: false,
                cellStyle: { fontWeight: 'bold' },
            },
            ...monthColumns,
            {
                headerName: t('summary.total'),
                field: '__sum',
                minWidth: 120,
                editable: false,
                pinned: 'right',
                type: 'numericColumn',
                cellStyle: { fontWeight: 'bold', textAlign: 'right' },
                valueFormatter: formatByRow,
            },
        ],
        [monthColumns, formatByRow, t]
    );

    const handleCellValueChanged = useCallback(
        (event: CellValueChangedEvent) => {
            const { data: row, colDef } = event;
            const rowKey = row?.rowKey as string | undefined;
            if (!rowKey || rowKey === COST_ROW || !colDef.field || !MONTH_KEY.test(colDef.field)) return;
            const month = colDef.field;
            const value = row[month] == null ? null : Number(row[month]);
            pending.set(config.id, rowKey, month, value, baseFor(rowKey)?.get(month));
        },
        [pending.set, config.id, baseFor]
    );

    // The same guard the Overview's Save has, for BOTH min(0) endpoints: letting one request fail
    // its schema while the other commits is exactly the half-save this blocks.
    const firstIssue = useMemo(() => {
        const negative = (kind: string) =>
            pending.edits.some((edit) => edit.kind === kind && edit.value != null && Number(edit.value) < 0);
        if (negative(VOLUME_KIND)) return t('validation.volumeNegative');
        if (negative('contractABTEdits')) return t('validation.abtNegative');
        return null;
    }, [pending.edits, t]);

    const handleSave = useCallback(async () => {
        const edits = pending.edits;
        const configEdits = edits.filter((edit) => edit.kind !== VOLUME_KIND);
        const volumeEdits = edits.filter((edit) => edit.kind === VOLUME_KIND);

        setIsSaving(true);
        pending.submit(edits);
        // One request per resource, like the page's Save — no endpoint spans master data and the
        // version's volumes. Both are month-deltas, so a retry after a partial failure re-sends the
        // already-committed half as no-ops.
        const [configResult, volumeResult] = await Promise.allSettled([
            configEdits.length ? saveConfigs(billingConfigPayload(configEdits)) : Promise.resolve(null),
            volumeEdits.length ? saveVolumes(forecastPayload(volumeEdits, VOLUME_KIND)) : Promise.resolve(null),
        ]);
        // Awaited BEFORE closing, so the grid behind the dialog re-renders from fresh data, not the
        // stale cache. The mutation hooks' own onSettled invalidations have already started these
        // fetches; cancelRefetch: false joins them instead of cancelling and restarting them, so
        // each key is fetched once per save.
        await Promise.all(
            [
                [QueryKey.BudgetBillingConfigs],
                [QueryKey.BudgetSummary, versionId],
                [QueryKey.BudgetCalculationResults, versionId],
            ].map((queryKey) => queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false }))
        );
        setIsSaving(false);

        // The bulk endpoint intentionally SKIPS a config deleted since load and reports only the ids
        // it wrote — a fulfilled response without this row is not a success (same rule as the Prices
        // & ABT page's save bar).
        const configWritten =
            !configEdits.length ||
            (configResult.status === 'fulfilled' &&
                ((configResult.value ?? []) as Array<{ id: number }>).some((row) => Number(row.id) === config.id));

        if (configResult.status === 'fulfilled' && volumeResult.status === 'fulfilled' && configWritten) {
            onSaved?.(
                config.id,
                volumeEdits.map((edit) => edit.key)
            );
            onClose();
            return;
        }
        if (configResult.status === 'fulfilled' && !configWritten) {
            dispatchErrorNotification({ message: t('hint.someRowsNotSaved') });
        }
        // A failed mutation already toasted. Keep every edit for the retry — the committed half's
        // months are unchanged now, so re-sending them writes nothing.
        pending.settle(edits, new Set());
    }, [pending, saveConfigs, saveVolumes, versionId, onSaved, onClose, config.id, dispatchErrorNotification, t]);

    // CALL-816: acknowledging freezes the row's CURRENT saved missing months — which is why both ack
    // buttons are disabled while the dialog holds unsaved edits: the state on screen would not be
    // the state being (un)acknowledged. The warning stays green until a new month goes missing.
    const handleAcknowledge = useCallback(async () => {
        if (!ackInfo) return;
        try {
            await saveAck({ billingConfigId: config.id, missingMonths: ackInfo.canonicalMissing });
            onClose();
        } catch {
            // The mutation's onError already raised the toast; keep the dialog open for a retry.
        }
    }, [ackInfo, saveAck, config.id, onClose]);

    const handleRemoveAck = useCallback(async () => {
        try {
            await deleteAck({ billingConfigId: config.id });
            onClose();
        } catch {
            // As above — toasted by the mutation, dialog stays open.
        }
    }, [deleteAck, config.id, onClose]);

    // Escape and backdrop clicks land here too. While a request is out they are ignored: closing
    // unmounts the dialog's pending store, so a failure could no longer keep the edits for a retry,
    // and a success could no longer run onSaved — the page would re-save superseded volume edits.
    const handleClose = useCallback(() => {
        if (!isBusy) onClose();
    }, [isBusy, onClose]);

    const label = [
        [config.partnerName, config.locationName].filter(Boolean).join(' - '),
        [
            config.workingUnitLocation?.workingUnit?.worktype?.worktype,
            config.workingUnitLocation?.workingUnit?.channel?.name,
            config.workingUnitLocation?.workingUnit?.task?.name,
        ]
            .filter(Boolean)
            .join(' - '),
    ]
        .filter(Boolean)
        .join(' — ');

    const hasWarning = Boolean(ackInfo && !isAcked && ackInfo.canonicalMissing.length > 0);

    return (
        <ResponsiveDialog
            open
            onClose={handleClose}
            // The row's identity belongs in the header — safe there without a notranslate wrapper
            // because the dialog remounts per config, so the title never updates in place.
            title={`${t('detail.dialogTitle')}: ${label}`}
            maxWidth="xl"
            fullWidth
            showCloseIcon
            // One action row for the warning's two answers plus the plain exit. Not the standard
            // confirmation pair: Save and Acknowledge are alternatives, not a confirm/cancel.
            actionButtons={
                <DialogActions>
                    <Button onClick={handleClose} variant="outlined" disabled={isBusy}>
                        <span>{t('action.close')}</span>
                    </Button>
                    {!isReadOnly && isAcked && (
                        <Button onClick={handleRemoveAck} variant="outlined" disabled={isBusy || pending.isDirty}>
                            <span>{t('ack.remove')}</span>
                        </Button>
                    )}
                    {!isReadOnly && hasWarning && (
                        <Button onClick={handleAcknowledge} variant="outlined" disabled={isBusy || pending.isDirty}>
                            <span>{t('ack.confirm')}</span>
                        </Button>
                    )}
                    {!isReadOnly && (
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={isBusy || !pending.isDirty || Boolean(firstIssue)}
                        >
                            <span className="notranslate">
                                {t('action.saveChangesCount', { count: pending.count })}
                            </span>
                        </Button>
                    )}
                </DialogActions>
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                <div className="budget-grid">
                    <TableGridItem
                        height="auto"
                        domLayout="autoHeight"
                        rowData={rowData}
                        columnDefs={columnDefs}
                        getRowId={(params) => String(params.data.rowKey)}
                        onCellValueChanged={handleCellValueChanged}
                        loading={isPeriodLoading}
                        defaultColDef={{ sortable: false, filter: false, resizable: true, suppressMenu: true }}
                        cellSelection
                    />
                </div>
                {/* CALL-824: five cues share these cells; the legend names them, like the grid pages. */}
                <CellLegend
                    cues={
                        isReadOnly
                            ? ['defaultValue']
                            : ['editable', 'unsaved', 'manualOverride', 'gap', 'defaultValue', 'defaultOverride']
                    }
                />
                {firstIssue && (
                    <Typography variant="body2" color="error">
                        <span>{firstIssue}</span>
                    </Typography>
                )}
                {/* CALL-816: the warning the grid above visualizes, and what acknowledging it means. */}
                {hasWarning && (
                    <>
                        <Typography variant="body2">
                            <span>{t('ack.dialogIntro', { count: ackInfo!.canonicalMissing.length })}</span>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <span>{t('ack.missingMonthsLabel')} </span>
                            <span className="notranslate">{formatMonths(ackInfo!.canonicalMissing)}</span>
                        </Typography>
                        {ack && (
                            <Typography variant="body2" color="warning.main">
                                <span>{t('ack.outdatedDialogHint')}</span>
                            </Typography>
                        )}
                    </>
                )}
                {isAcked && (
                    <>
                        <Typography variant="body2">
                            <span className="notranslate">
                                {t('ack.acknowledged', {
                                    name: ack!.acknowledgedByName || '—',
                                    date: dayjs(ack!.updatedAt).format(DATE_FORMATS.DATE),
                                })}
                            </span>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <span>{t('ack.acknowledgedMonthsLabel')} </span>
                            <span className="notranslate">{formatMonths(ack!.missingMonths)}</span>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <span>{t('ack.removeHint')}</span>
                        </Typography>
                    </>
                )}
                {/* Both ack actions apply to the SAVED state, so unsaved edits block them. */}
                {!isReadOnly && pending.isDirty && (hasWarning || isAcked) && (
                    <Typography variant="body2" color="text.secondary">
                        <span>{t('detail.saveBeforeAck')}</span>
                    </Typography>
                )}
            </Box>
        </ResponsiveDialog>
    );
};

// Remounted per open — and per version and config, so a ?versionId= switch under an open dialog can
// never write one version's figures into another. The pending store starts fresh each time; closing
// IS the discard.
const ConfigDetailDialog = (props: ConfigDetailDialogProps) =>
    props.data ? (
        <ConfigDetailDialogInner key={`${props.versionId}:${props.data.config.id}`} {...props} data={props.data} />
    ) : null;

export default ConfigDetailDialog;
